import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import InfoIcon from '@src/quince-site-integrated/src/assets/info_reward.svg'

import Icon from '../../icon'
import { TOP_TIP_POSITION, Tooltip } from '../../tooltip'

import styles from './reviewMetaRow.module.scss'

const REWARD_PROGRAMME_TOOLTIP_DEFAULT =
  'Customer participated in our reviews reward programme and received reward points.'

const DEFAULT_REWARD_PROGRAMME_LEARN_MORE_URL =
  '/contact-us?a=Earn-Quince-Points---id--nsJ1AIfETgOb0Pmj_sgktQ&trafficSource=footer'

// @i18n-ignore
const REWARD_PROGRAMME_INFO_ICON_TEST_ID = 'reward-programme-info-icon'

export interface ReviewMetaRowProps {
  /** Country display name resolved from API regionId (e.g. "United States"). */
  reviewedInCountry?: string
  /** When true, shows reward programme indicator with tooltip. */
  isIncentivized?: boolean
  /** URL for "Learn more" in reward programme tooltip. Falls back to default Gladly HC article. */
  rewardProgrammeLearnMoreUrl?: string
}

/**
 * Shared meta row shown below review body in both ReviewItem (CSR) and
 * ProductReviewHighLight (SSR highlights). Renders "Reviewed in {country}"
 * and/or the reward programme indicator with tooltip.
 *
 * Gated by CONFIG_DISPLAY_LOCALISED_REVIEW_SIZE feature flag.
 */
const ReviewMetaRow = ({ reviewedInCountry, isIncentivized, rewardProgrammeLearnMoreUrl }: ReviewMetaRowProps) => {
  const { t } = useTranslation('common')
  const localeConfig = useStatsigDynamicLocaleConfig()
  const hasReviewedInCountry = Boolean(reviewedInCountry)
  const showRow = localeConfig.displayLocalisedReviewSize && (hasReviewedInCountry || Boolean(isIncentivized))

  const [isRewardTooltipVisible, setIsRewardTooltipVisible] = useState(false)
  const rewardTooltipWrapperRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!isRewardTooltipVisible) return
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (rewardTooltipWrapperRef.current?.contains(target)) return
      setIsRewardTooltipVisible(false)
    }
    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('touchstart', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [isRewardTooltipVisible])

  if (!showRow) return null

  return (
    <div className={styles.reviewMetaRow}>
      {hasReviewedInCountry ? (
        <span className={styles.reviewMetaItem}>{t('reviewed_in', { country: reviewedInCountry })}</span>
      ) : null}
      {isIncentivized ? (
        <>
          {hasReviewedInCountry ? <span className={styles.reviewMetaSeparator} aria-hidden /> : null}
          <span className={styles.reviewMetaItem}>
            {t('reward_programme')}
            <span
              ref={rewardTooltipWrapperRef}
              className={styles.rewardProgrammeTooltip}
              onClick={() => {
                setIsRewardTooltipVisible(prev => !prev)
              }}
            >
              <Icon
                iconSvg={InfoIcon}
                className={styles.rewardProgrammeInfoIcon}
                dataTestId={REWARD_PROGRAMME_INFO_ICON_TEST_ID}
              />
              <Tooltip
                isVisible={isRewardTooltipVisible}
                topTipPosition={TOP_TIP_POSITION.BOTTOM}
                className={styles.rewardProgrammeTooltipContent}
              >
                <span className={styles.rewardProgrammeTooltipContentInner}>
                  {t('reward_programme_tooltip_text', { defaultValue: REWARD_PROGRAMME_TOOLTIP_DEFAULT })}
                  <a
                    href={rewardProgrammeLearnMoreUrl ?? DEFAULT_REWARD_PROGRAMME_LEARN_MORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.rewardProgrammeLearnMore}
                  >
                    {t('learn_more')}
                  </a>
                </span>
              </Tooltip>
            </span>
          </span>
        </>
      ) : null}
    </div>
  )
}

export default ReviewMetaRow
